import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  CircularProgress,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  AccountTree as TreeIcon,
  List as ListIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { costCentersService } from '../services/costCenters.service';
import {
  CostCenterDto,
  CostCenterTreeDto
} from '../types/costCenters.types';
import CostCenterDialog from '../components/costCenters/CostCenterDialog';
import CostCenterTree from '../components/costCenters/CostCenterTree';

const CostCentersPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [viewMode, setViewMode] = useState<'list' | 'tree'>('tree');
  const [costCenters, setCostCenters] = useState<CostCenterDto[]>([]);
  const [treeData, setTreeData] = useState<CostCenterTreeDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCostCenter, setSelectedCostCenter] = useState<CostCenterDto | null>(null);

  useEffect(() => {
    loadData();
  }, [includeInactive, viewMode]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (viewMode === 'tree') {
        const data = await costCentersService.getTree(includeInactive);
        setTreeData(data);
      } else {
        const data = await costCentersService.getAll(includeInactive);
        setCostCenters(data);
      }
    } catch (err: any) {
      const errorMsg = isRTL ? 'فشل تحميل مراكز التكلفة' : 'Failed to load cost centers';
      setError(err.response?.data?.message || errorMsg);
      console.error('Failed to load cost centers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedCostCenter(null);
    setCreateDialogOpen(true);
  };

  const handleEdit = async (costCenter: CostCenterDto) => {
    try {
      setLoading(true);
      setError(null);
      // Fetch full details including notes
      const fullData = await costCentersService.getById(costCenter.costCenterId);
      setSelectedCostCenter(fullData);
      setEditDialogOpen(true);
    } catch (err: any) {
      const errorMsg = isRTL ? 'فشل تحميل بيانات مركز التكلفة' : 'Failed to load cost center data';
      setError(err.response?.data?.message || errorMsg);
      console.error('Failed to load cost center:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (costCenter: CostCenterDto) => {
    setSelectedCostCenter(costCenter);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCostCenter) return;

    try {
      setLoading(true);
      setError(null);
      await costCentersService.delete(selectedCostCenter.costCenterId);
      const successMsg = isRTL ? 'تم حذف مركز التكلفة بنجاح' : 'Cost center deleted successfully';
      setSuccess(successMsg);
      setDeleteDialogOpen(false);
      setSelectedCostCenter(null);
      loadData();
    } catch (err: any) {
      const errorMsg = isRTL ? 'فشل حذف مركز التكلفة' : 'Failed to delete cost center';
      setError(err.response?.data?.message || errorMsg);
      console.error('Failed to delete cost center:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSuccess = () => {
    const successMsg = isRTL ? 'تم إنشاء مركز التكلفة بنجاح' : 'Cost center created successfully';
    setSuccess(successMsg);
    setCreateDialogOpen(false);
    loadData();
  };

  const handleEditSuccess = () => {
    const successMsg = isRTL ? 'تم تحديث مركز التكلفة بنجاح' : 'Cost center updated successfully';
    setSuccess(successMsg);
    setEditDialogOpen(false);
    loadData();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            {isRTL ? 'مراكز التكلفة' : 'Cost Centers'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading}
            >
              {isRTL ? 'تحديث' : 'Refresh'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              {isRTL ? 'مركز تكلفة جديد' : 'New Cost Center'}
            </Button>
          </Box>
        </Box>

        {/* View Mode Tabs */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs
            value={viewMode}
            onChange={(_, newValue) => setViewMode(newValue)}
          >
            <Tab 
              value="tree" 
              label={isRTL ? 'العرض الشجري' : 'Tree View'}
              icon={<TreeIcon />} 
              iconPosition="start"
            />
            <Tab 
              value="list" 
              label={isRTL ? 'عرض القائمة' : 'List View'}
              icon={<ListIcon />} 
              iconPosition="start"
            />
          </Tabs>

          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
            }
            label={isRTL ? 'إظهار غير النشط' : 'Show Inactive'}
          />
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Content */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress />
          </Box>
        ) : viewMode === 'tree' ? (
          treeData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <Typography color="text.secondary">
                {isRTL ? 'لا توجد بيانات' : 'No data available'}
              </Typography>
            </Box>
          ) : (
            <CostCenterTree
              data={treeData}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
            />
          )
        ) : (
          /* List View */
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{isRTL ? 'الرمز' : 'Code'}</TableCell>
                  <TableCell>{isRTL ? 'الاسم' : 'Name'}</TableCell>
                  <TableCell>{isRTL ? 'مركز التكلفة الأب' : 'Parent'}</TableCell>
                  <TableCell>{isRTL ? 'المستوى' : 'Level'}</TableCell>
                  <TableCell>{isRTL ? 'نهائي' : 'Final'}</TableCell>
                  <TableCell>{isRTL ? 'الحالة' : 'Status'}</TableCell>
                  <TableCell align="center">{isRTL ? 'الإجراءات' : 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {costCenters.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      {isRTL ? 'لا توجد بيانات' : 'No data available'}
                    </TableCell>
                  </TableRow>
                ) : (
                  costCenters.map((cc) => (
                    <TableRow key={cc.costCenterId} hover>
                      <TableCell>{cc.costCenterCode}</TableCell>
                      <TableCell>
                        {isRTL ? cc.costCenterNameAr : cc.costCenterName}
                      </TableCell>
                      <TableCell>
                        {cc.parentCostCenterId 
                          ? (isRTL ? cc.parentCostCenterNameAr : cc.parentCostCenterName)
                          : '-'
                        }
                      </TableCell>
                      <TableCell>{cc.level}</TableCell>
                      <TableCell>
                        <Chip
                          label={cc.isFinalCostCenter 
                            ? (isRTL ? 'نهائي' : 'Final')
                            : (isRTL ? 'غير نهائي' : 'Non-Final')
                          }
                          size="small"
                          color={cc.isFinalCostCenter ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cc.isActive 
                            ? (isRTL ? 'نشط' : 'Active')
                            : (isRTL ? 'غير نشط' : 'Inactive')
                          }
                          size="small"
                          color={cc.isActive ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title={isRTL ? 'تعديل' : 'Edit'}>
                            <IconButton size="small" onClick={() => handleEdit(cc)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={isRTL ? 'حذف' : 'Delete'}>
                            <IconButton size="small" onClick={() => handleDeleteClick(cc)} color="error">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create Dialog */}
      {createDialogOpen && (
        <CostCenterDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Edit Dialog */}
      {editDialogOpen && selectedCostCenter && (
        <CostCenterDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSuccess={handleEditSuccess}
          costCenter={selectedCostCenter}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          {isRTL ? 'تأكيد الحذف' : 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {isRTL 
              ? `هل أنت متأكد من حذف مركز التكلفة ${selectedCostCenter?.costCenterCode}?`
              : `Are you sure you want to delete cost center ${selectedCostCenter?.costCenterCode}?`
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={loading}>
            {isRTL ? 'حذف' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CostCentersPage;